import { type FC, forwardRef, useContext, useState } from 'react';
import { Button } from '@accure/ui/components/button/button-catalyst';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * TEMP FIX: This component fetches penalty chart data to get historical-only penalties
 * instead of using sohPenaltyMax which incorrectly includes predictive penalties.
 * Remove the penalty chart data fetch and revert to using sohPenaltyMax once
 * backend fixes the penalty_max field in platform_core_warranty_unit_status.
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip } from '@/components/ui/tooltip';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';
import { cls } from '@/utils/cls';
import { formatEnergy } from '@/utils/format/battery';
import { plural } from '@/utils/format/label';
import { formatNumber, formatPercentage } from '@/utils/format/number';
import type { WarrantyIDStatus } from '@/warranty/domain/entities/warranty-id-status';
import {
  type PenaltyOverviewChartData,
  type WarrantyPenalty,
} from '@/warranty/domain/entities/warranty-penalty-chart-data';
import { WarrantyCoverageStatus } from '@/warranty/domain/entities/warranty-status';
import { IWarrantyRepository } from '@/warranty/domain/repositories/warranty.repository';

import { WarrantyDetailsContext } from '../../pages/warranty-details';
import { GetAllWarrantyIDStatus } from '../../queries/get-all-warranty-id-status';
import { GetWarrantyPenaltyChartData } from '../../queries/get-warranty-penalty-chart-data';
import { ComplianceStatusBadge } from '../ComplianceStatusBadge';
import { OverviewTableSkeleton } from '../skeletons/OverviewTableSkeleton';
import { TooltipAdditionalInfo } from '../tooltip-additional-info';
import { WarrantyCoverageStatusBadge } from '../WarrantyCoverageStatusBadge';

import { EmptyOverviewTable } from './empty-overview-table';

// Define NumberTag Component for reusable tag styling
export const NumberTag: FC<{ text: string }> = ({ text }) => (
  <span className="rounded bg-gray-700 px-1.5 py-0.5 font-mono text-sm font-medium text-gray-400">
    {text}
  </span>
);

export type OverviewTableProps = {
  filters: IWarrantyRepository.Filters;
};
// TEMP FIX: Helper function to get historical penalty for a specific asset
const getHistoricalPenalty = (
  penaltyData:
    | IWarrantyRepository.GetWarrantyPenaltyChartDataOutput
    | undefined,
  fullFrontendId: string,
): number => {
  if (!penaltyData) return 0;

  const assetPenalties = penaltyData.find(
    (item: PenaltyOverviewChartData) => item.fullFrontendId === fullFrontendId,
  );

  if (!assetPenalties) return 0;

  // Sum all historical penalties (yearToDate) for this asset
  return assetPenalties.penalties.reduce(
    (total: number, penalty: WarrantyPenalty) => total + penalty.yearToDate,
    0,
  );
};

// Process warranty data with penalties and SOH calculations
type ProcessedWarrantyData = {
  warrantyStatus: WarrantyIDStatus;
  penalty: number;
  adjustedSoH: number;
  isOpenCase: boolean;
  hasPenalty: boolean;
  displaySoH: number;
  sohFallback: boolean;
}[];

function processWarrantyData(
  warrantyData: IWarrantyRepository.GetAllWarrantyIDStatusOutput,
  penaltyData:
    | IWarrantyRepository.GetWarrantyPenaltyChartDataOutput
    | undefined,
): ProcessedWarrantyData {
  const processedData = warrantyData.map((warrantyStatus: WarrantyIDStatus) => {
    // TEMP FIX: Use historical penalty instead of total penalty
    const penalty = getHistoricalPenalty(
      penaltyData,
      warrantyStatus.fullFrontendId,
    );
    const adjustedSoH = warrantyStatus.sohGuaranteed;

    // check if case open
    const isOpenCase =
      warrantyStatus.warrantyCoverageStatus ===
        WarrantyCoverageStatus.CASE_OPEN &&
      warrantyStatus.sohAccure < adjustedSoH;

    const hasPenalty = penalty > 0;
    const sohFallback = warrantyStatus.sohAccure == null;

    // If fallback, force 100; otherwise use actual value
    const displaySoH = sohFallback ? 100 : warrantyStatus.sohAccure!;

    return {
      warrantyStatus,
      penalty,
      adjustedSoH,
      isOpenCase,
      hasPenalty,
      displaySoH,
      sohFallback,
    };
  });

  return processedData;
}

export const OverviewTable = forwardRef<HTMLDivElement, OverviewTableProps>(
  function ({ filters }, ref) {
    const me = useMe();
    const asset = useContext(WarrantyDetailsContext);
    const featureFlag = useFeatureFlag();

    // use warrantyAlerts featureFlag for suggested action
    const showSuggestedAction = featureFlag('warrantyAlerts');

    // TEMP FIX: Use this beautiful hack to show the container tag and soh columns
    let showContainerTag = false;
    let showSohColumns = true;
    const showCyclesColumn = true;
    let showRackWithIssuesColumn = true;
    if (me.config.id === '69') {
      showContainerTag = true;
      showSohColumns = false;
      showRackWithIssuesColumn = false;
    }

    // State: whether the table is expanded to full height or constrained with its own scroll
    const [expanded, setExpanded] = useState(false);

    // --- fetch the data ---
    const warrantyOverview = GetAllWarrantyIDStatus.useQuery({
      filters,
      assetFullFrontendId: asset!.fullFrontendId,
      assetId: asset!.id,
      customerId: me.config.id,
    });

    // TEMP FIX: Fetch penalty chart data to get historical-only penalties
    // until backend fixes penalty_max to exclude predictive penalties
    const penaltyChartData = GetWarrantyPenaltyChartData.useQuery({
      filters,
      assetFullFrontendId: asset!.fullFrontendId,
      assetId: asset!.id,
      customerId: me.config.id,
    });

    if (warrantyOverview.isLoading || penaltyChartData.isLoading)
      return <OverviewTableSkeleton />;
    if (warrantyOverview.isError)
      return <div>Error loading Warranty overview</div>;
    if (penaltyChartData.isError) return <div>Error loading penalty data</div>;
    if (!warrantyOverview.data || warrantyOverview.data.length === 0) {
      return <EmptyOverviewTable showSohColumns={showSohColumns} />;
    }

    const processedData = processWarrantyData(
      warrantyOverview.data,
      penaltyChartData.data,
    );

    return (
      <>
        {/* Scrollable container when not expanded */}
        <div
          ref={ref}
          className={cls('relative w-full', {
            'max-h-[30vh] overflow-y-auto': !expanded,
          })}
        >
          <Table
            striped
            dense
            bleed
            className="w-full table-auto overflow-x-auto"
          >
            <TableHead className="sticky top-0 bg-gray-100">
              <TableRow>
                <TableHeader className="text-left">ID</TableHeader>
                <TableHeader narrow className="text-center">
                  Coverage Status
                </TableHeader>
                <TableHeader narrow className="text-center">
                  Compliance Status
                </TableHeader>
                {showRackWithIssuesColumn && (
                  <TableHeader narrow className="text-center">
                    <TooltipAdditionalInfo
                      content={
                        <>
                          Number of{' '}
                          {plural(me.config.unit.labels.name).toLowerCase()}{' '}
                          having performance / operation issues
                        </>
                      }
                    >
                      {plural(me.config.unit.labels.name)} /w issues
                    </TooltipAdditionalInfo>
                  </TableHeader>
                )}
                {showSuggestedAction && (
                  <TableHeader wide className="text-center">
                    Suggested Action
                  </TableHeader>
                )}
                <TableHeader narrow className="text-center">
                  Age in Years
                </TableHeader>
                {showCyclesColumn && (
                  <TableHeader narrow className="text-center">
                    Total Cycles
                  </TableHeader>
                )}
                <TableHeader narrow className="text-center">
                  Total Throughput
                </TableHeader>
                {showContainerTag && (
                  <TableHeader narrow className="text-center">
                    Average
                    <br /> Container TAG
                  </TableHeader>
                )}
                {showSohColumns && (
                  <TableHeader narrow className="text-center">
                    <TooltipAdditionalInfo
                      content={
                        <>
                          Defined as minimum SOH of underlying{' '}
                          {plural(me.config.unit.labels.name).toLowerCase()}
                        </>
                      }
                    >
                      Current min. SOH
                    </TooltipAdditionalInfo>
                  </TableHeader>
                )}
                {showSohColumns && (
                  <TableHeader narrow className="text-center">
                    <TooltipAdditionalInfo
                      content={
                        <>
                          Defined as
                          <br />
                          <NumberTag text="guaranteed SOH - penalties" />
                          <br />
                          (excluding predicted penalties)
                        </>
                      }
                    >
                      Guaranteed SOH
                    </TooltipAdditionalInfo>
                  </TableHeader>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {processedData.map(
                ({
                  warrantyStatus,
                  penalty,
                  adjustedSoH,
                  isOpenCase,
                  hasPenalty,
                  displaySoH,
                  sohFallback,
                }) => (
                  <TableRow
                    key={warrantyStatus.id}
                    className="hover:bg-gray-50"
                  >
                    <TableCell wide justify="left">
                      {warrantyStatus.fullFrontendId}
                    </TableCell>
                    <TableCell
                      narrow
                      justify="left"
                      className="whitespace-nowrap"
                    >
                      <WarrantyCoverageStatusBadge
                        status={warrantyStatus.warrantyCoverageStatus}
                      />
                    </TableCell>
                    <TableCell
                      narrow
                      justify="left"
                      className="whitespace-nowrap"
                    >
                      <ComplianceStatusBadge
                        status={warrantyStatus.complianceStatus}
                      />
                    </TableCell>
                    {showRackWithIssuesColumn && (
                      <TableCell narrow justify="center">
                        {formatNumber(
                          warrantyStatus.nUnitsPerformanceIssues,
                          0,
                          0,
                        )}{' '}
                        /{' '}
                        {formatNumber(
                          warrantyStatus.nUnitsOperationIssues,
                          0,
                          0,
                        )}
                      </TableCell>
                    )}
                    {showSuggestedAction && (
                      <TableCell wide justify="left">
                        {warrantyStatus.suggestedAction}
                      </TableCell>
                    )}
                    <TableCell narrow justify="center">
                      {formatNumber(warrantyStatus.ageInYears)}
                    </TableCell>
                    {showCyclesColumn && (
                      <TableCell narrow justify="center">
                        {formatNumber(warrantyStatus.cyclesTotal)}
                      </TableCell>
                    )}
                    <TableCell
                      narrow
                      justify={
                        warrantyStatus.throughputTotal ? 'right' : 'center'
                      }
                    >
                      <span className="whitespace-nowrap">
                        {warrantyStatus.throughputTotal
                          ? formatEnergy(
                              warrantyStatus.throughputTotal,
                              me.config.powerUnit,
                            )
                          : 'N/A'}
                      </span>
                    </TableCell>
                    {showContainerTag ? (
                      <TableCell narrow justify="center">
                        {warrantyStatus.availability ? (
                          warrantyStatus.availability.threshold &&
                          warrantyStatus.availability.value <
                            warrantyStatus.availability.threshold ? (
                            <Tooltip
                              content={`Below the warranty treshold of ${formatPercentage(warrantyStatus.availability.threshold / 100, 0, 1)}`}
                              clickToKeepOpen
                              delay
                            >
                              <span className="whitespace-nowrap font-semibold underline">
                                {formatPercentage(
                                  warrantyStatus.availability.value / 100,
                                )}
                              </span>
                            </Tooltip>
                          ) : (
                            <span className="whitespace-nowrap">
                              {formatPercentage(
                                warrantyStatus.availability.value / 100,
                              )}
                            </span>
                          )
                        ) : null}
                      </TableCell>
                    ) : null}
                    {/* Current min. SOH */}
                    {showSohColumns && (
                      <TableCell narrow justify="center">
                        {sohFallback ? (
                          <Tooltip
                            delay
                            content="Preliminary SOH assumed at 100%"
                          >
                            <span className="whitespace-nowrap italic text-gray-500">
                              {formatPercentage(displaySoH / 100, 0, 0)}
                            </span>
                          </Tooltip>
                        ) : isOpenCase ? (
                          <div className="flex items-center justify-center gap-1 rounded border border-yellow-300 bg-yellow-50 px-1 py-0.5 font-semibold text-yellow-900">
                            <span className="whitespace-nowrap">
                              {formatPercentage(displaySoH / 100)}
                            </span>
                            <span className="text-sm text-yellow-700">
                              &lt;
                            </span>
                          </div>
                        ) : (
                          <span className="whitespace-nowrap">
                            {formatPercentage(displaySoH / 100)}
                          </span>
                        )}
                      </TableCell>
                    )}
                    {/* Guaranteed SOH */}
                    {showSohColumns && (
                      <TableCell justify="left">
                        {hasPenalty ? (
                          <Tooltip
                            delay
                            content="SOH adjusted by historical penalty only (excl. predictive)"
                          >
                            <div className="whitespace-nowrap">
                              <span className="whitespace-nowrap font-semibold underline">
                                {adjustedSoH
                                  ? formatPercentage(adjustedSoH / 100)
                                  : 'N/A'}
                              </span>
                              {penalty >= 0.01 && (
                                <span className="ml-2 whitespace-nowrap text-sm font-semibold text-red-600">
                                  (-{formatPercentage(penalty / 100)})
                                </span>
                              )}
                            </div>
                          </Tooltip>
                        ) : (
                          // If there is no penalty, just show the base or adjusted (they are the same in that case)
                          <span className="whitespace-nowrap">
                            {adjustedSoH
                              ? formatPercentage(adjustedSoH / 100)
                              : 'N/A'}
                          </span>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
          {/* Fade effect at the bottom when not expanded - sticky positioned */}
          {!expanded && processedData.length > 10 && (
            <div className="pointer-events-none sticky inset-x-0 bottom-0 -mt-32 h-32 bg-gradient-to-t from-white to-transparent" />
          )}
        </div>

        {/* Expand / Collapse toggle */}
        {processedData.length > 10 && (
          <div
            className={`flex justify-center text-center ${!expanded ? 'relative z-10 -mt-8' : 'mt-3'}`}
          >
            <Button
              type="button"
              plain
              onClick={() => setExpanded((prev) => !prev)}
              className="flex items-center gap-1.5 rounded-full border border-gray-100 bg-white/80 px-4 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-all duration-150 hover:text-primary-600 hover:shadow"
            >
              {expanded ? <ChevronUp /> : <ChevronDown />}
              {expanded ? 'Show less' : `Show all (${processedData.length})`}
            </Button>
          </div>
        )}
      </>
    );
  },
);
OverviewTable.displayName = 'OverviewTable';
